import { formatThousands } from "@trex-2b/libs";

import { isRecord } from "@/lib/utils";

/**
 * PRISM report → Match Report UI 的数据适配层（纯函数，无 React）。
 * 输入：getPrismReport 返回的 JSON；输出：TMatchReportData，供 MatchReportSummary / MatchReportInsightCardSlot 直接渲染。
 *
 * ┌─────────────────────────────────────────────────────────────────────────────┐
 * │ 顶层数据转换（parsePrismReportPayload）                                      │
 * ├──────────────────┬──────────────────────────────────────────────────────────┤
 * │ projectName      │ profile.project_name → handle → ""                        │
 * │ summaryText      │ report.summary → payload.summary → ""                     │
 * │ insights[]       │ report.insights[] → normalizeInsight × N                  │
 * └──────────────────┴──────────────────────────────────────────────────────────┘
 *
 * ┌─────────────────────────────────────────────────────────────────────────────┐
 * │ 单条 insight 通用路径                                                        │
 * │ insights[i] + tags[]（tag_id 匹配）→ normalizeInsight → TMatchReportInsight  │
 * │   1. title / body / tag_id 取自 insight                                     │
 * │   2. viz_type 取自 insight 或 matchedTag（normalizeVisualizationType）       │
 * │   3. viz_data 取自 tag.viz_data_fields → insight.viz_data_fields → evidence  │
 * │   4. 按 visualizationType 调用对应 map* 函数挂载卡片专用字段                    │
 * └─────────────────────────────────────────────────────────────────────────────┘
 *
 * ┌─────────────────────────────────────────────────────────────────────────────┐
 * │ 卡片类型 → UI 组件 → 转换函数 → 输出字段                                      │
 * ├──────────────────┬────────────────────┬──────────────────────────────────────┤
 * │ horizontal_bar   │ BarInsightCard     │ mapHorizontalBarEvidence             │
 * │ (bar_benchmark)  │                    │ → metricLabel, barRows[]             │
 * ├──────────────────┼────────────────────┼──────────────────────────────────────┤
 * │ side_by_side     │ SideBySideCard     │ mapSideBySideEvidence              │
 * │                  │                    │ → leftStat, rightStat                │
 * ├──────────────────┼────────────────────┼──────────────────────────────────────┤
 * │ checklist        │ ChecklistCard      │ mapChecklistVizDataFields            │
 * │ (checklist_card) │                    │ → checklistItems[], checklistVariant │
 * ├──────────────────┼────────────────────┼──────────────────────────────────────┤
 * │ venn             │ VennInsightCard    │ mapVennEvidence                      │
 * │ (venn_diagram)   │                    │ → vennCircles[]                      │
 * ├──────────────────┼────────────────────┼──────────────────────────────────────┤
 * │ unknown          │ Typewriter only    │ （无 map*）仅 title + body            │
 * └──────────────────┴────────────────────┴──────────────────────────────────────┘
 */

type TMatchReportInsightVisualizationType =
  | "venn"
  | "horizontal_bar"
  | "side_by_side"
  | "checklist"
  | "unknown";

type TMatchReportChecklistItem = {
  label: string;
  description: string;
  isCompleted: boolean;
};

type TMatchReportChecklistVariant = "no_audit" | "default";

type TMatchReportBarRow = {
  label: string;
  value: string;
  fillClassName: string;
  fillWidthPercent: number;
  isPrimary?: boolean;
};

type TMatchReportComparisonStat = {
  value: string;
  label: string;
};

type TMatchReportVennCircle = {
  lineOne: string;
  lineTwo: string;
  fillClassName: string;
  borderClassName: string;
  legendDotClassName: string;
  color?: string;
};

/**
 * 单张洞察卡归一化后的 props。
 * 各 viz 专用字段互斥，由 visualizationType 决定 MatchReportInsightCardSlot 分发到哪张卡片。
 */
type TMatchReportInsight = {
  /** 所有卡片共用：洞察标题（打字机） */
  title: string;
  /** 所有卡片共用：洞察正文（打字机，支持 **bold** 分段） */
  body: string;
  /** 卡片分发键：决定调用哪个 map* 函数及 UI 子组件 */
  visualizationType: TMatchReportInsightVisualizationType;
  /**
   * insight.tag_id 透传。
   * - checklist：tagId === "NO_AUDIT" → 2 项 Gap 布局
   * - side_by_side：tagId === "TIER_S_BACKER" → UI 标题绿色特例
   */
  tagId?: string;
  // ── 横条卡 horizontal_bar 专用 ──
  /** viz_data.metric_label → BarInsightCard 图表上方指标名 */
  metricLabel?: string;
  /** mapHorizontalBarEvidence → BarInsightCard.rows */
  barRows?: TMatchReportBarRow[];
  // ── 并排卡 side_by_side 专用 ──
  /** mapSideBySideEvidence → SideBySideCard 左侧（UI 固定绿色） */
  leftStat?: TMatchReportComparisonStat;
  /** mapSideBySideEvidence → SideBySideCard 右侧（UI 固定红色） */
  rightStat?: TMatchReportComparisonStat;
  // ── 韦恩图卡 venn 专用 ──
  /** mapVennEvidence → VennInsightCard.vennCircles */
  vennCircles?: TMatchReportVennCircle[];
  // ── 清单卡 checklist 专用 ──
  /** mapChecklistVizDataFields → ChecklistInsightCard.checklistItems */
  checklistItems?: TMatchReportChecklistItem[];
  /** resolveChecklistVariant：NO_AUDIT → "no_audit"（2 项），其余 → "default"（3 项） */
  checklistVariant?: TMatchReportChecklistVariant;
};

/** parsePrismReportPayload 的最终输出，对应 MatchReportSummary 的三块渲染区域 */
type TMatchReportData = {
  /** 摘要区标题旁的项目名：profile.project_name → handle */
  projectName: string;
  /** 摘要区打字机正文：report.summary */
  summaryText: string;
  /** 洞察卡列表：每条 insights[] 经 normalizeInsight 归一化 */
  insights: TMatchReportInsight[];
};

const VENN_CIRCLE_STYLE_PRESETS: Omit<
  TMatchReportVennCircle,
  "lineOne" | "lineTwo"
>[] = [
  {
    fillClassName: "bg-[rgba(142,162,144,0.4)]",
    borderClassName: "border-[#8ea290]",
    legendDotClassName: "bg-[#8ea290]",
  },
  {
    fillClassName: "bg-[rgba(181,210,255,0.4)]",
    borderClassName: "border-[#b5d2ff]",
    legendDotClassName: "bg-[#b5d2ff]",
  },
  {
    fillClassName: "bg-[rgba(229,222,189,0.4)]",
    borderClassName: "border-[rgba(229,222,189,0.9)]",
    legendDotClassName: "bg-[rgba(229,222,189,0.9)]",
  },
];

function formatReportMetricValue(value: unknown): string {
  const numericValue = readNumericMetricValue(value);
  if (numericValue != null) {
    return formatThousands(numericValue);
  }
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  return "—";
}

/** 解开 SSE/嵌套响应的外层 report 包装，统一后续读取 profile / tags / insights 的入口 */
function resolveReportPayload(
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const nestedReport = payload.report;
  if (
    isRecord(nestedReport) &&
    (isRecord(nestedReport.profile) || Array.isArray(nestedReport.tags))
  ) {
    return nestedReport;
  }

  return payload;
}

function readReportSection(
  payload: Record<string, unknown>,
): Record<string, unknown> | null {
  const nestedReport = payload.report;
  if (!isRecord(nestedReport)) {
    return null;
  }

  const innerReport = nestedReport.report;
  if (isRecord(innerReport)) {
    return innerReport;
  }

  return nestedReport;
}

/** 【摘要区】report.summary → payload.summary → "" */
function readReportSummary(payload: Record<string, unknown>): string {
  const reportSection = readReportSection(payload);
  if (reportSection && typeof reportSection.summary === "string") {
    return reportSection.summary.trim();
  }
  if (typeof payload.summary === "string") {
    return payload.summary.trim();
  }
  return "";
}

/** 【摘要区】profile.project_name → handle → ""；同时作为横条卡项目行默认 label */
function readProjectName(payload: Record<string, unknown>): string {
  const profile = payload.profile;
  if (isRecord(profile) && typeof profile.project_name === "string") {
    const projectName = profile.project_name.trim();
    if (projectName) {
      return projectName;
    }
  }
  if (typeof payload.handle === "string") {
    const handle = payload.handle.trim();
    if (handle) {
      return handle;
    }
  }
  return "";
}

/** profile.sector；横条卡 Floor/Leaders 行 label 拼接用（如 "DeFi Floor"） */
function readProfileSector(payload: Record<string, unknown>): string {
  const profile = payload.profile;
  if (isRecord(profile) && typeof profile.sector === "string") {
    return profile.sector.trim();
  }
  return "";
}

/** 读取 insights[] 原始数组，供逐条 normalizeInsight 转换 */
function readInsightsArray(payload: Record<string, unknown>): unknown[] {
  const reportSection = readReportSection(payload);
  const candidates: unknown[] = [reportSection?.insights, payload.insights];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate;
    }
  }

  return [];
}

/**
 * tag_id → tag 记录索引。
 * normalizeInsight 用 insight.tag_id 查找 matchedTag，优先从 matchedTag 读取 viz_type / viz_data_fields。
 */
function buildTagEvidenceMap(
  payload: Record<string, unknown>,
): Map<string, Record<string, unknown>> {
  const tagCandidates: unknown[] = [
    payload.tags,
    readReportSection(payload)?.tags,
  ];
  const tagEvidenceMap = new Map<string, Record<string, unknown>>();

  for (const candidate of tagCandidates) {
    if (!Array.isArray(candidate)) {
      continue;
    }

    for (const rawTag of candidate) {
      if (!isRecord(rawTag)) {
        continue;
      }
      const tagId = String(rawTag.tag_id ?? rawTag.id ?? "").trim();
      if (!tagId) {
        continue;
      }
      tagEvidenceMap.set(tagId, rawTag);
    }
  }

  return tagEvidenceMap;
}

function readNumericMetricValue(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const trimmed = value.trim().replace(/%$/, "");
    if (!trimmed) {
      return null;
    }
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return null;
}

function computeBarFillWidthPercent(value: number, maxValue: number): number {
  if (maxValue <= 0) {
    return 0;
  }
  return Math.min(100, (Math.abs(value) / maxValue) * 100);
}

type TBarVizFieldRole = "project" | "floor" | "leaders";

type TBarVizFieldItem = {
  fieldKey: string;
  value: unknown;
  label?: string;
};

const BAR_VIZ_META_KEYS = new Set(["metric_label", "metriclabel"]);

/**
 * 【横条卡】field_key 角色判定：
 *   含 p25 → floor（赛道下限）
 *   含 p75 → leaders（赛道上限）
 *   其余   → project（当前项目值，isPrimary）
 */
function resolveBarFieldRole(fieldKey: string): TBarVizFieldRole {
  const normalizedKey = fieldKey.toLowerCase();
  if (normalizedKey.includes("p25")) {
    return "floor";
  }
  if (normalizedKey.includes("p75")) {
    return "leaders";
  }
  return "project";
}

function parseBarVizFieldItem(
  rawItem: Record<string, unknown>,
): TBarVizFieldItem | null {
  const fieldKey = String(
    rawItem.field_key ??
      rawItem.fieldKey ??
      rawItem.key ??
      rawItem.name ??
      rawItem.metric ??
      rawItem.field ??
      "",
  ).trim();
  if (!fieldKey) {
    return null;
  }

  const value = rawItem.value ?? rawItem.field_value ?? rawItem.fieldValue;
  if (value == null) {
    return null;
  }

  const label = String(rawItem.label ?? rawItem.display_label ?? "").trim();
  return {
    fieldKey,
    value,
    ...(label ? { label } : {}),
  };
}

/**
 * 【横条卡】将 viz_data_fields 统一解析为 { fieldKey, value, label? }[]。
 * 支持三种后端形态：数组 items、vizData.items、扁平 key-value 对象。
 */
function resolveBarVizFieldItems(
  vizDataFields: unknown,
  vizData: Record<string, unknown> | null,
): TBarVizFieldItem[] {
  if (Array.isArray(vizDataFields)) {
    return vizDataFields.flatMap((rawItem) =>
      isRecord(rawItem) ? (parseBarVizFieldItem(rawItem) ?? []) : [],
    );
  }

  if (vizData && Array.isArray(vizData.items)) {
    return vizData.items.flatMap((rawItem) =>
      isRecord(rawItem) ? (parseBarVizFieldItem(rawItem) ?? []) : [],
    );
  }

  const recordSource = isRecord(vizDataFields)
    ? vizDataFields
    : isRecord(vizData)
      ? vizData
      : null;
  if (!recordSource) {
    return [];
  }

  return Object.entries(recordSource).flatMap(([fieldKey, rawValue]) => {
    if (BAR_VIZ_META_KEYS.has(fieldKey.trim().toLowerCase())) {
      return [];
    }
    if (isRecord(rawValue)) {
      const parsed = parseBarVizFieldItem({ ...rawValue, field_key: fieldKey });
      return parsed ? [parsed] : [];
    }
    if (rawValue == null) {
      return [];
    }
    return [{ fieldKey, value: rawValue }];
  });
}

/**
 * 各卡片共用的 viz 原始对象解析（normalizeInsight 第 3 步）：
 *   1. tag/insight 的 viz_data_fields（对象）→ 直接作为 vizData
 *   2. viz_data_fields 为数组 → 包装为 { items }
 *   3. 回退 tag.evidence / insight.evidence
 */
function resolveVizDataRecord(
  matchedTag: Record<string, unknown> | undefined,
  rawInsight: Record<string, unknown>,
  evidence: Record<string, unknown> | undefined,
): Record<string, unknown> | null {
  const vizDataFields =
    matchedTag?.viz_data_fields ?? rawInsight.viz_data_fields;

  if (isRecord(vizDataFields)) {
    return vizDataFields;
  }

  if (Array.isArray(vizDataFields) && vizDataFields.length > 0) {
    return { items: vizDataFields };
  }

  return evidence ?? null;
}

/** 【卡片分发】从 insight 或 matchedTag 读取 viz_type，经 normalizeVisualizationType 对齐别名 */
function resolveVisualizationType(
  rawInsight: Record<string, unknown>,
  matchedTag: Record<string, unknown> | undefined,
): TMatchReportInsightVisualizationType {
  const rawTypeCandidates: unknown[] = [
    rawInsight.viz_type,
    rawInsight.visualization_type,
    rawInsight.visualizationType,
    matchedTag?.viz_type,
    matchedTag?.visualization_type,
  ];

  for (const rawType of rawTypeCandidates) {
    const normalizedType = normalizeVisualizationType(rawType);
    if (normalizedType !== "unknown") {
      return normalizedType;
    }
  }

  return "unknown";
}

/** 对齐后端 viz_type 别名（如 bar_benchmark、checklist_card、side_by_side_card） */
function normalizeVisualizationType(
  rawType: unknown,
): TMatchReportInsightVisualizationType {
  if (typeof rawType !== "string") {
    return "unknown";
  }

  const normalizedType = rawType.trim().toLowerCase();
  if (normalizedType === "venn" || normalizedType === "venn_diagram") {
    return "venn";
  }
  if (
    normalizedType === "horizontal_bar" ||
    normalizedType === "bar_benchmark" ||
    normalizedType === "bar"
  ) {
    return "horizontal_bar";
  }
  if (
    normalizedType === "side_by_side" ||
    normalizedType === "side_by_side_card"
  ) {
    return "side_by_side";
  }
  if (normalizedType === "checklist" || normalizedType === "checklist_card") {
    return "checklist";
  }

  return "unknown";
}

/**
 * 【横条卡 horizontal_bar / bar_benchmark → MatchReportBarInsightCard】
 *
 * 输入（viz_data_fields 扁平对象示例）：
 *   { twitter_followers: 1471523, "sector.follower_p25": 30000, "sector.follower_p75": 300000 }
 *
 * 转换规则：
 *   field_key 无 p25/p75 → project 行（isPrimary，条色随与 floor/leaders 比较：低红/高绿/中灰）
 *   field_key 含 p25     → floor 行（label 默认 "{sector} Floor"）
 *   field_key 含 p75     → leaders 行（固定绿色条）
 *   metric_label         → metricLabel（图表上方指标名）
 *
 * 输出 → TMatchReportInsight：
 *   metricLabel?: string
 *   barRows[]: { label, value, fillClassName, fillWidthPercent, isPrimary? }
 */
function mapHorizontalBarEvidence(
  vizData: Record<string, unknown> | null,
  vizDataFields: unknown,
  fallbackProjectLabel: string,
  profileSector: string,
): Pick<TMatchReportInsight, "metricLabel" | "barRows"> {
  const parsedFields = resolveBarVizFieldItems(vizDataFields, vizData).map(
    (field) => ({
      role: resolveBarFieldRole(field.fieldKey),
      value: field.value,
      ...(field.label ? { label: field.label } : {}),
    }),
  );

  const projectField = parsedFields.find((field) => field.role === "project");
  const floorField = parsedFields.find((field) => field.role === "floor");
  const leadersField = parsedFields.find((field) => field.role === "leaders");
  const orderedFields = [projectField, floorField, leadersField].flatMap(
    (field) => (field ? [field] : []),
  );

  if (orderedFields.length === 0) {
    return {};
  }

  const maxValue = orderedFields.reduce((currentMax, field) => {
    const numericValue = readNumericMetricValue(field.value);
    return numericValue == null
      ? currentMax
      : Math.max(currentMax, Math.abs(numericValue));
  }, 0);

  const projectValue = projectField
    ? readNumericMetricValue(projectField.value)
    : null;
  const floorValue = floorField
    ? readNumericMetricValue(floorField.value)
    : null;
  const leadersValue = leadersField
    ? readNumericMetricValue(leadersField.value)
    : null;

  const barRows: TMatchReportBarRow[] = orderedFields.map((field) => {
    const numericValue = readNumericMetricValue(field.value);
    const isPrimary = field.role === "project";

    // 项目条：低于 floor 红、高于 leaders 绿，否则灰；leaders 条恒绿
    let fillClassName = "bg-[#a4a3a2]";
    if (field.role === "leaders") {
      fillClassName = "bg-[#42925d]";
    } else if (
      isPrimary &&
      projectValue != null &&
      floorValue != null &&
      leadersValue != null
    ) {
      if (projectValue < floorValue) {
        fillClassName = "bg-[#c95b50]";
      } else if (projectValue > leadersValue) {
        fillClassName = "bg-[#42925d]";
      }
    }

    let label = field.label;
    if (!label) {
      if (field.role === "project") {
        label = fallbackProjectLabel;
      } else if (field.role === "floor") {
        label = profileSector ? `${profileSector} Floor` : "Floor";
      } else {
        label = profileSector ? `${profileSector} Leaders` : "Leaders";
      }
    }

    return {
      label,
      value: formatReportMetricValue(field.value),
      fillClassName,
      fillWidthPercent:
        numericValue == null
          ? 0
          : computeBarFillWidthPercent(numericValue, maxValue),
      ...(isPrimary ? { isPrimary: true } : {}),
    };
  });

  const metricLabel =
    isRecord(vizData) && typeof vizData.metric_label === "string"
      ? vizData.metric_label.trim()
      : isRecord(vizData) && typeof vizData.metricLabel === "string"
        ? vizData.metricLabel.trim()
        : undefined;

  return {
    ...(metricLabel ? { metricLabel } : {}),
    barRows,
  };
}

/** 融资金额紧凑格式化（$1.5B / $1.5M / $10K / $500） */
const COMPACT_USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
});

/**
 * 【并排卡 side_by_side — 融资回退格式】
 * 当 viz_data 无 left/right 对象时启用（如 TIER_S_BACKER tag）：
 *   funding_total_usd → leftStat.value（formatCompactUsd）+ label "Raised"
 *   backer_tier       → rightStat.value（原文字符串）+ label "Backer tier"
 *
 * 输出 → TMatchReportInsight：leftStat?, rightStat?
 */
function mapFundingSideBySideEvidence(
  vizData: Record<string, unknown>,
): Pick<TMatchReportInsight, "leftStat" | "rightStat"> {
  const fundingTotalUsd = readNumericMetricValue(vizData.funding_total_usd);
  const backerTier = String(vizData.backer_tier ?? "").trim();
  if (fundingTotalUsd == null && !backerTier) {
    return {};
  }

  return {
    ...(fundingTotalUsd != null
      ? {
          leftStat: {
            value: COMPACT_USD_FORMATTER.format(fundingTotalUsd),
            label: "Raised",
          },
        }
      : {}),
    ...(backerTier
      ? {
          rightStat: {
            value: backerTier,
            label: "Backer tier",
          },
        }
      : {}),
  };
}

/**
 * 【并排卡 side_by_side → MatchReportSideBySideInsightCard】
 *
 * 转换优先级：
 *   1. viz_data.left / viz_data.right 对象 → { value: formatReportMetricValue, label }
 *   2. 缺省 → mapFundingSideBySideEvidence（funding_total_usd + backer_tier）
 *
 * 输出 → TMatchReportInsight：
 *   leftStat?:  { value, label }  — UI 固定绿色
 *   rightStat?: { value, label }  — UI 固定红色
 *   tagId 保留给 UI 侧 TIER_S_BACKER 标题色特例
 */
function mapSideBySideEvidence(
  vizData: Record<string, unknown> | null,
): Pick<TMatchReportInsight, "leftStat" | "rightStat"> {
  if (!vizData) {
    return {};
  }

  const left = isRecord(vizData.left) ? vizData.left : null;
  const right = isRecord(vizData.right) ? vizData.right : null;

  if (left || right) {
    return {
      ...(left
        ? {
            leftStat: {
              value: formatReportMetricValue(left.value),
              label: String(left.label ?? ""),
            },
          }
        : {}),
      ...(right
        ? {
            rightStat: {
              value: formatReportMetricValue(right.value),
              label: String(right.label ?? ""),
            },
          }
        : {}),
    };
  }

  return mapFundingSideBySideEvidence(vizData);
}

/** 【清单卡】数组项完成态：is_completed / completed / status === "completed|done|complete" */
function readChecklistItemCompleted(rawItem: Record<string, unknown>): boolean {
  if (
    rawItem.is_completed === true ||
    rawItem.isCompleted === true ||
    rawItem.completed === true
  ) {
    return true;
  }

  const status = String(rawItem.status ?? "")
    .trim()
    .toLowerCase();
  return status === "completed" || status === "done" || status === "complete";
}

/**
 * 【清单卡 checklist — NO_AUDIT 分支 → checklistVariant: "no_audit"】
 * 固定 2 项 Gap 布局，从扁平 viz_data_fields 枚举：
 *   onchain_state === "mainnet"  → Mainnet.isCompleted = true
 *   audit_status  === "completed" → Security Audit.isCompleted = true
 */
function mapNoAuditChecklistItems(
  vizData: Record<string, unknown>,
): TMatchReportChecklistItem[] {
  const onchainState = String(vizData.onchain_state ?? "")
    .trim()
    .toLowerCase();
  const auditStatus = String(vizData.audit_status ?? "")
    .trim()
    .toLowerCase();

  return [
    {
      label: "Mainnet",
      description:
        "Product is live on a production chain with real users and real assets.",
      isCompleted: onchainState === "mainnet",
    },
    {
      label: "Security Audit",
      description: "Contracts have been audited by a recognized security firm.",
      isCompleted: auditStatus === "completed",
    },
  ];
}

/**
 * 【清单卡 checklist — 默认分支 → checklistVariant: "default"】
 * 固定 3 项 Ladder 进度轴，从扁平 viz_data_fields 枚举：
 *   onchain_state in (testnet, mainnet) → Testnet.isCompleted = true
 *   onchain_state === "mainnet"         → Mainnet.isCompleted = true
 *   token_state in (live, listed)       → Token / TGE.isCompleted = true
 */
function mapDefaultChecklistItems(
  vizData: Record<string, unknown>,
): TMatchReportChecklistItem[] {
  const onchainState = String(vizData.onchain_state ?? "")
    .trim()
    .toLowerCase();
  const tokenState = String(vizData.token_state ?? "")
    .trim()
    .toLowerCase();

  return [
    {
      label: "Testnet",
      description:
        "Product is on a test chain with simulated assets. Nothing real is being judged yet.",
      isCompleted: onchainState === "testnet" || onchainState === "mainnet",
    },
    {
      label: "Mainnet",
      description:
        "Product is live with real users and real on-chain activity. The record is forming.",
      isCompleted: onchainState === "mainnet",
    },
    {
      label: "Token / TGE",
      description:
        "Token is trading on at least one exchange. Price and volume are the data now.",
      isCompleted: tokenState === "live" || tokenState === "listed",
    },
  ];
}

/** 【清单卡】扁平 viz_data_fields 按 tagId 路由到 NO_AUDIT（2 项）或 default（3 项）枚举 */
function mapFlatChecklistVizDataFields(
  vizData: Record<string, unknown>,
  tagId: string,
): TMatchReportChecklistItem[] {
  if (tagId === "NO_AUDIT") {
    return mapNoAuditChecklistItems(vizData);
  }

  return mapDefaultChecklistItems(vizData);
}

/**
 * 【清单卡 checklist → MatchReportChecklistInsightCard】
 *
 * 转换优先级：
 *   1. viz_data_fields / items / checklist_items 为数组
 *      → 逐项解析 { label, description, isCompleted }
 *   2. 无数组项 → mapFlatChecklistVizDataFields（按 tagId 走 NO_AUDIT 或 default 枚举）
 *
 * 输出 → TMatchReportInsight：
 *   checklistItems[]: { label, description, isCompleted }
 *   checklistVariant: tagId === "NO_AUDIT" ? "no_audit" : "default"
 */
function mapChecklistVizDataFields(
  vizData: Record<string, unknown> | null,
  vizDataFields: unknown,
  tagId: string,
): TMatchReportChecklistItem[] {
  const checklistItemsSource = Array.isArray(vizDataFields)
    ? vizDataFields
    : vizData && Array.isArray(vizData.items)
      ? vizData.items
      : vizData && Array.isArray(vizData.checklist_items)
        ? vizData.checklist_items
        : [];

  if (checklistItemsSource.length === 0) {
    const flatSource = isRecord(vizDataFields)
      ? vizDataFields
      : isRecord(vizData)
        ? vizData
        : null;
    return flatSource ? mapFlatChecklistVizDataFields(flatSource, tagId) : [];
  }

  return checklistItemsSource.flatMap((rawItem) => {
    if (!isRecord(rawItem)) {
      return [];
    }

    const label = String(
      rawItem.label ?? rawItem.name ?? rawItem.title ?? rawItem.key ?? "",
    ).trim();
    if (!label) {
      return [];
    }

    const description = String(
      rawItem.description ?? rawItem.desc ?? rawItem.body ?? "",
    ).trim();

    return [
      {
        label,
        description,
        isCompleted: readChecklistItemCompleted(rawItem),
      } satisfies TMatchReportChecklistItem,
    ];
  });
}

/** 【清单卡】tagId === "NO_AUDIT" → 2 项 Gap 布局，其余 → 3 项 Ladder 布局 */
function resolveChecklistVariant(tagId: string): TMatchReportChecklistVariant {
  return tagId === "NO_AUDIT" ? "no_audit" : "default";
}

/** 【韦恩图卡】project_identity 字符串格式化：defi_protocol → "Defi Protocol" */
function formatIdentityLabel(identity: string): string {
  return identity
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(
      (part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`,
    )
    .join(" ");
}

/** 【韦恩图卡】圆标签拆为双行：首词 → lineOne，其余 → lineTwo */
function splitVennLabel(label: string): { lineOne: string; lineTwo: string } {
  const labelParts = label.split(/\s+/);
  if (labelParts.length <= 1) {
    return { lineOne: label, lineTwo: "" };
  }
  return {
    lineOne: labelParts[0] ?? label,
    lineTwo: labelParts.slice(1).join(" "),
  };
}

/**
 * 【韦恩图卡 venn / venn_diagram → MatchReportVennInsightCard】
 *
 * 圆数据来源（按优先级）：
 *   1. viz_data.circles
 *   2. viz_data.venn_circles
 *   3. viz_data.items
 *   4. viz_data.project_identity 字符串数组 → formatIdentityLabel 转 label
 *
 * 每个圆转换：
 *   label → splitVennLabel → { lineOne, lineTwo }
 *   index → VENN_CIRCLE_STYLE_PRESETS 循环配色（绿/蓝/琥珀，最多 3 色）
 *   color → 可选覆盖预设
 *
 * 输出 → TMatchReportInsight：
 *   vennCircles[]: { lineOne, lineTwo, fillClassName, borderClassName, legendDotClassName, color? }
 */
function mapVennEvidence(
  vizData: Record<string, unknown> | null,
): Pick<TMatchReportInsight, "vennCircles"> {
  const circleCandidates: unknown[] = [
    vizData?.circles,
    vizData?.venn_circles,
    vizData?.items,
  ];

  let rawCircles: unknown[] = [];
  for (const candidate of circleCandidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      rawCircles = candidate;
      break;
    }
  }

  if (
    rawCircles.length === 0 &&
    vizData &&
    Array.isArray(vizData.project_identity)
  ) {
    rawCircles = vizData.project_identity.flatMap((rawIdentity) => {
      const identity = String(rawIdentity ?? "").trim();
      return identity ? [{ label: formatIdentityLabel(identity) }] : [];
    });
  }

  if (rawCircles.length === 0) {
    return {};
  }

  const vennCircles = rawCircles.flatMap((rawCircle, index) => {
    if (!isRecord(rawCircle)) {
      return [];
    }
    const label = String(rawCircle.label ?? rawCircle.name ?? "").trim();
    if (!label) {
      return [];
    }
    const { lineOne, lineTwo } = splitVennLabel(label);
    const stylePreset =
      VENN_CIRCLE_STYLE_PRESETS[index % VENN_CIRCLE_STYLE_PRESETS.length];
    const color =
      typeof rawCircle.color === "string" && rawCircle.color.trim()
        ? rawCircle.color.trim()
        : undefined;

    return [
      {
        lineOne,
        lineTwo,
        ...stylePreset,
        ...(color ? { color } : {}),
      } satisfies TMatchReportVennCircle,
    ];
  });

  return vennCircles.length > 0 ? { vennCircles } : {};
}

/**
 * 单条 insight 归一化入口：insights[i] → TMatchReportInsight。
 *
 * 通用字段（所有卡片共用）：
 *   title, body ← insight
 *   tagId       ← insight.tag_id
 *   visualizationType ← resolveVisualizationType
 *
 * 卡片专用字段按 visualizationType 分发（见文件头映射表）。
 */
function normalizeInsight(
  rawInsight: unknown,
  tagEvidenceMap: Map<string, Record<string, unknown>>,
  fallbackProjectLabel: string,
  profileSector: string,
): TMatchReportInsight | null {
  if (!isRecord(rawInsight)) {
    return null;
  }

  const title = String(rawInsight.title ?? "").trim();
  const body = String(rawInsight.body ?? "").trim();
  if (!title || !body) {
    return null;
  }

  const tagId = String(rawInsight.tag_id ?? "").trim();
  const matchedTag = tagId ? tagEvidenceMap.get(tagId) : undefined;
  const evidence = isRecord(matchedTag?.evidence)
    ? matchedTag?.evidence
    : isRecord(rawInsight.evidence)
      ? rawInsight.evidence
      : undefined;
  const visualizationType = resolveVisualizationType(rawInsight, matchedTag);
  const vizData = resolveVizDataRecord(matchedTag, rawInsight, evidence);
  const vizDataFields =
    matchedTag?.viz_data_fields ?? rawInsight.viz_data_fields;

  const baseInsight: TMatchReportInsight = {
    title,
    body,
    visualizationType,
    ...(tagId ? { tagId } : {}),
  };

  // ── 清单卡 checklist → mapChecklistVizDataFields ──
  if (visualizationType === "checklist") {
    const checklistItems = mapChecklistVizDataFields(
      vizData,
      vizDataFields,
      tagId,
    );
    return {
      ...baseInsight,
      ...(checklistItems.length > 0 ? { checklistItems } : {}),
      checklistVariant: resolveChecklistVariant(tagId),
    };
  }

  // ── 横条卡 horizontal_bar → mapHorizontalBarEvidence ──
  if (visualizationType === "horizontal_bar") {
    return {
      ...baseInsight,
      ...mapHorizontalBarEvidence(
        vizData,
        vizDataFields,
        fallbackProjectLabel,
        profileSector,
      ),
    };
  }

  // ── 并排卡 side_by_side → mapSideBySideEvidence ──
  if (visualizationType === "side_by_side") {
    return {
      ...baseInsight,
      ...mapSideBySideEvidence(vizData),
    };
  }

  // ── 韦恩图卡 venn → mapVennEvidence ──
  if (visualizationType === "venn") {
    return {
      ...baseInsight,
      ...mapVennEvidence(vizData),
    };
  }

  // ── unknown：仅 title + body，MatchReportInsightCardSlot 回退为纯打字机 ──
  return baseInsight;
}

/**
 * PRISM report 解析入口。
 *
 * 数据流：
 *   payload
 *     → resolveReportPayload（解开嵌套 report）
 *     → projectName    【摘要区】readProjectName
 *     → summaryText    【摘要区】readReportSummary
 *     → profileSector  【横条卡】Floor/Leaders label 拼接
 *     → tagEvidenceMap 【所有卡片】tag_id 索引
 *     → insights[]     【洞察卡】normalizeInsight × N
 */
function parsePrismReportPayload(
  payload: Record<string, unknown>,
): TMatchReportData {
  const resolvedPayload = resolveReportPayload(payload);
  const projectName = readProjectName(resolvedPayload);
  const profileSector = readProfileSector(resolvedPayload);
  const summaryText = readReportSummary(resolvedPayload);
  const tagEvidenceMap = buildTagEvidenceMap(resolvedPayload);
  const normalizedInsights = readInsightsArray(resolvedPayload).flatMap(
    (rawInsight) => {
      const insight = normalizeInsight(
        rawInsight,
        tagEvidenceMap,
        projectName,
        profileSector,
      );
      return insight ? [insight] : [];
    },
  );

  return {
    projectName,
    summaryText,
    insights: normalizedInsights,
  };
}

/** session / 消息 data 中已持久化的 report 快照是否可直出 UI（跳过 getPrismReport） */
function isPersistedMatchReportData(value: unknown): value is TMatchReportData {
  if (!value || typeof value !== "object") {
    return false;
  }
  const record = value as TMatchReportData;
  return (
    typeof record.projectName === "string" &&
    typeof record.summaryText === "string" &&
    Array.isArray(record.insights)
  );
}

export type { TMatchReportData, TMatchReportInsight };
export { isPersistedMatchReportData, parsePrismReportPayload };
